import { useState, useEffect, useMemo } from 'react';
import { ITimelineEvent, IMemoryStats } from '@/types/timeline';
import { calculateMemoryStats, formatMemorySize, isMemoryPressure } from '@/utils/memoryManager';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  Button,
  Badge,
  Progress,
  Alert,
  AlertDescription,
} from 'debug-time-machine-ui';
import { 
  Database,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  Trash2,
  RotateCcw,
  Activity,
  HardDrive,
  Zap,
  Clock
} from 'lucide-react';

interface MemoryMonitorProps {
  events: ITimelineEvent[];
  onEventsUpdated?: (events: ITimelineEvent[]) => void;
  onManualCleanup?: () => void;
  className?: string;
  compact?: boolean; // 컴팩트 모드 추가
}

interface MemoryTrend {
  timestamp: number;
  totalMemory: number;
  eventCount: number;
  compressedCount: number;
}

const MemoryMonitor = ({ 
  events, 
  onEventsUpdated, 
  onManualCleanup,
  className = '',
  compact = false 
}: MemoryMonitorProps) => {
  const [memoryHistory, setMemoryHistory] = useState<MemoryTrend[]>([]);
  const [lastCleanupTime, setLastCleanupTime] = useState<number>(Date.now());

  // 현재 메모리 통계 계산
  const currentStats = useMemo(() => calculateMemoryStats(events), [events]);

  // 메모리 압박 상태 확인
  const isUnderPressure = useMemo(() => isMemoryPressure(currentStats), [currentStats]);

  // 메모리 사용률 계산 (퍼센트)
  const memoryUsagePercent = useMemo(() => {
    const maxMemory = 50 * 1024 * 1024; // 50MB 제한
    return Math.min((currentStats.totalMemoryUsage / maxMemory) * 100, 100);
  }, [currentStats.totalMemoryUsage]);

  // 이벤트 수 사용률 계산
  const eventCountPercent = useMemo(() => {
    const maxEvents = 1000;
    return Math.min((currentStats.totalEvents / maxEvents) * 100, 100);
  }, [currentStats.totalEvents]);

  // 메모리 히스토리 업데이트
  useEffect(() => {
    const now = Date.now();
    const newTrend: MemoryTrend = {
      timestamp: now,
      totalMemory: currentStats.totalMemoryUsage,
      eventCount: currentStats.totalEvents,
      compressedCount: currentStats.compressedEvents,
    };

    setMemoryHistory(prev => {
      const updated = [...prev, newTrend];
      // 최근 20개 항목만 유지
      return updated.slice(-20);
    });
  }, [currentStats]);

  // 메모리 트렌드 계산
  const memoryTrend = useMemo(() => {
    if (memoryHistory.length < 2) return 'stable';
    
    const recent = memoryHistory.slice(-5);
    const totalChange = recent[recent.length - 1].totalMemory - recent[0].totalMemory;
    const threshold = 1024 * 1024; // 1MB
    
    if (totalChange > threshold) return 'increasing';
    if (totalChange < -threshold) return 'decreasing';
    return 'stable';
  }, [memoryHistory]);

  // 수동 정리 처리
  const handleManualCleanup = () => {
    if (onManualCleanup) {
      onManualCleanup();
      setLastCleanupTime(Date.now());
    }
  };

  // 압축률 계산
  const compressionRatio = useMemo(() => {
    if (currentStats.totalEvents === 0) return 0;
    return (currentStats.compressedEvents / currentStats.totalEvents) * 100;
  }, [currentStats]);

  // 시간별 분포 계산
  const hourlyDistribution = useMemo(() => {
    const now = Date.now();
    const hours = 24;
    const distribution = new Array(hours).fill(0);
    
    events.forEach(event => {
      const hoursAgo = Math.floor((now - event.timestamp) / (1000 * 60 * 60));
      if (hoursAgo < hours) {
        distribution[hours - 1 - hoursAgo]++;
      }
    });
    
    return distribution;
  }, [events]);

  const getTrendIcon = () => {
    switch (memoryTrend) {
      case 'increasing':
        return <TrendingUp className="w-4 h-4 text-red-500" />;
      case 'decreasing':
        return <TrendingDown className="w-4 h-4 text-emerald-500" />;
      default:
        return <Activity className="w-4 h-4 text-blue-500" />;
    }
  };

  const getHealthStatus = () => {
    if (isUnderPressure) return { status: 'Critical', color: 'text-red-600', icon: AlertTriangle };
    if (memoryUsagePercent > 70) return { status: 'Warning', color: 'text-amber-600', icon: AlertTriangle };
    return { status: 'Good', color: 'text-emerald-600', icon: CheckCircle2 };
  };

  const health = getHealthStatus();

  // 컴팩트 모드 렌더링
  if (compact) {
    return (
      <div className={`space-y-3 ${className}`}>
        {/* 메모리 압박 경고 */}
        {isUnderPressure && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-sm">
              High memory usage detected.
              <Button
                variant="outline"
                size="sm"
                className="ml-2 h-6 text-xs"
                onClick={handleManualCleanup}
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Clean Up
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* 핵심 메트릭 */}
        <div className="space-y-3">
          {/* 메모리 사용량 */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm font-medium flex items-center gap-2">
                <Database className="w-4 h-4" />
                Memory Usage
              </span>
              <div className="flex items-center gap-1">
                {getTrendIcon()}
                <Badge 
                  variant={health.status === 'Good' ? 'secondary' : health.status === 'Warning' ? 'outline' : 'destructive'}
                  className={`${health.color} text-xs h-5`}
                >
                  {health.status}
                </Badge>
              </div>
            </div>
            <Progress value={memoryUsagePercent} className="h-2 mb-1" />
            <div className="text-xs text-muted-foreground text-right">
              {formatMemorySize(currentStats.totalMemoryUsage)} / 50MB ({memoryUsagePercent.toFixed(1)}%)
            </div>
          </div>

          {/* 이벤트 수 */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm font-medium flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Events
              </span>
              <span className="text-xs text-muted-foreground">
                {currentStats.totalEvents} / 1000
              </span>
            </div>
            <Progress value={eventCountPercent} className="h-2 mb-1" />
            <div className="text-xs text-muted-foreground text-right">
              {currentStats.compressedEvents} compressed ({compressionRatio.toFixed(1)}%)
            </div>
          </div>

          {/* 간단한 통계 */}
          <div className="grid grid-cols-2 gap-2 pt-2">
            <div className="text-center p-2 bg-slate-50 dark:bg-slate-800 rounded">
              <div className="text-sm font-bold">{formatMemorySize(currentStats.averageEventSize)}</div>
              <div className="text-xs text-muted-foreground">Avg Size</div>
            </div>
            <div className="text-center p-2 bg-slate-50 dark:bg-slate-800 rounded">
              <div className="text-sm font-bold">
                {Math.round((Date.now() - lastCleanupTime) / 60000)}m
              </div>
              <div className="text-xs text-muted-foreground">Last Cleanup</div>
            </div>
          </div>

          {/* 컴팩트 액션 버튼 */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleManualCleanup}
              disabled={!onManualCleanup}
              className="flex-1 h-8 text-xs"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Clean Up
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.reload()}
              className="flex-1 h-8 text-xs"
            >
              <RotateCcw className="w-3 h-3 mr-1" />
              Reset
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // 기본 전체 모드 렌더링

  return (
    <div className={`space-y-4 ${className}`}>
      {/* 메모리 압박 경고 */}
      {isUnderPressure && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Memory usage is high. Consider cleaning up old events or reducing the data size.
            <Button
              variant="outline"
              size="sm"
              className="ml-2"
              onClick={handleManualCleanup}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clean Up Now
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* 메모리 개요 */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" />
              Memory Overview
            </CardTitle>
            <div className="flex items-center gap-2">
              {getTrendIcon()}
              <Badge 
                variant={health.status === 'Good' ? 'secondary' : health.status === 'Warning' ? 'outline' : 'destructive'}
                className={health.color}
              >
                {health.status}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* 메모리 사용량 */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Memory Usage</span>
              <span className="text-sm text-muted-foreground">
                {formatMemorySize(currentStats.totalMemoryUsage)} / 50MB
              </span>
            </div>
            <Progress value={memoryUsagePercent} className="h-2" />
          </div>

          {/* 이벤트 수 */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Event Count</span>
              <span className="text-sm text-muted-foreground">
                {currentStats.totalEvents} / 1000
              </span>
            </div>
            <Progress value={eventCountPercent} className="h-2" />
          </div>

          {/* 통계 그리드 */}
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div className="text-center">
              <div className="text-lg font-bold">{formatMemorySize(currentStats.averageEventSize)}</div>
              <div className="text-xs text-muted-foreground">Avg Event Size</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold">{compressionRatio.toFixed(1)}%</div>
              <div className="text-xs text-muted-foreground">Compressed</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 상세 통계 */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="w-5 h-5" />
            Detailed Statistics
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-3">
            {/* 총 이벤트 수 */}
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center gap-3">
                <Activity className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="font-medium">Total Events</p>
                  <p className="text-sm text-muted-foreground">All recorded events</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold">{currentStats.totalEvents}</p>
              </div>
            </div>

            {/* 압축된 이벤트 */}
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-purple-500" />
                <div>
                  <p className="font-medium">Compressed Events</p>
                  <p className="text-sm text-muted-foreground">Memory optimized</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold">{currentStats.compressedEvents}</p>
                <p className="text-xs text-muted-foreground">{compressionRatio.toFixed(1)}%</p>
              </div>
            </div>

            {/* 총 메모리 사용량 */}
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center gap-3">
                <Database className="w-5 h-5 text-emerald-500" />
                <div>
                  <p className="font-medium">Total Memory</p>
                  <p className="text-sm text-muted-foreground">Current usage</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold">{formatMemorySize(currentStats.totalMemoryUsage)}</p>
                <p className="text-xs text-muted-foreground">{memoryUsagePercent.toFixed(1)}% of limit</p>
              </div>
            </div>

            {/* 마지막 정리 시간 */}
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-slate-500" />
                <div>
                  <p className="font-medium">Last Cleanup</p>
                  <p className="text-sm text-muted-foreground">Memory management</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium">
                  {new Date(lastCleanupTime).toLocaleTimeString()}
                </p>
                <p className="text-xs text-muted-foreground">
                  {Math.round((Date.now() - lastCleanupTime) / 60000)} min ago
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 액션 버튼들 */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5" />
            Memory Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 gap-2">
            <Button
              variant="outline"
              onClick={handleManualCleanup}
              disabled={!onManualCleanup}
              className="w-full justify-start"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clean Up Old Events
            </Button>
            
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
              className="w-full justify-start"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset All Data
            </Button>
          </div>
          
          <div className="text-xs text-muted-foreground p-3 bg-slate-50 dark:bg-slate-800 rounded">
            <p>• Cleanup removes old and less important events</p>
            <p>• Reset clears all data and restarts monitoring</p>
            <p>• Automatic cleanup occurs at 900 events</p>
          </div>
        </CardContent>
      </Card>

      {/* 메모리 팁 */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">💡 Memory Tips</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground space-y-2">
          <p>• Large objects are automatically compressed</p>
          <p>• Bookmarked events are preserved during cleanup</p>
          <p>• Errors and critical events have higher priority</p>
          <p>• Memory usage is estimated from JSON size</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default MemoryMonitor;
